import React from 'react';
import { MessageSquare, Beaker, GraduationCap, Microscope, Code, Library, Users, Settings, Bot, FolderKanban, Paintbrush, Microchip, Bug, Waypoints, Monitor } from 'lucide-react';

import { SubscriptionTier } from '@/types';

export interface NavItem {
  path: string;
  label: string;
  icon?: React.ReactNode;
  active: boolean;
  hasChildren?: boolean;
  children?: NavItem[];
  onClick?: () => void;
  isNew?: boolean;
  requiredTier?: SubscriptionTier[];
}

export const getNavItems = (pathname: string, setShowBugReport: (show: boolean) => void, isMobile: boolean = false): NavItem[] => {
  const isChatsActive = pathname === '/' || pathname.startsWith('/chat');

  const applicationsChildren: NavItem[] = [
    {
      path: '/canvas',
      label: 'Canvas',
      icon: <Paintbrush size={16} />,
      active: pathname.startsWith('/canvas'),
    },

    {
      path: '/code',
      label: 'Code',
      icon: <Code size={16} />,
      active: pathname.startsWith('/code'),
    },
  ];

  const dataChildren: NavItem[] = [
    {
      path: '/knowledge',
      label: 'Knowledge Base',
      icon: <Library size={16} />,
      active: pathname.startsWith('/knowledge') || pathname.startsWith('/kb'),
    }

  ];

  const siteChildren: NavItem[] = [
    {
      path: '/intelligencexp',
      label: 'IntelligenceXP',
      icon: <Settings size={16} />,
      active: pathname.startsWith('/intelligencexp') || pathname.startsWith('/system-graph'),
    },
    {
      path: '/settings',
      label: 'User Settings',
      icon: <Settings size={16} />,
      active: pathname.startsWith('/settings'),
    },
    {
      path: '/users',
      label: 'Users',
      icon: <Users size={16} />,
      active: pathname.startsWith('/users'),
    },
    {
      path: '/access-control',
      label: 'Access Control',
      icon: <Settings size={16} />,
      active: pathname.startsWith('/access-control'),
      requiredTier: ['enterprise'], // optionally gate in UI; policy still gates server-side
    },
  ];

  const agentsChildren: NavItem[] = [
    {
      path: '/agents',
      label: 'Agent Marketplace',
      icon: <Bot size={16} />,
      active: pathname === '/agents' || pathname.startsWith('/agents/marketplace') || pathname.startsWith('/agents/use'),
    },
    {
      path: '/agents/custom',
      label: 'Custom Agents',
      icon: <Code size={16} />,
      active: pathname.startsWith('/agents/custom'),
    },
    {
      path: '/agents/professional',
      label: 'Professional',
      icon: <GraduationCap size={16} />,
      active: pathname.startsWith('/agents/professional'),
    },
    {
      path: '/agents/scientific',
      label: 'Scientific',
      icon: <Microscope size={16} />,
      active: pathname.startsWith('/agents/scientific'),
    },
  ];

  const baseItems: NavItem[] = [
    {
      path: '/',
      label: 'Chats',
      icon: <MessageSquare size={20} />,
      active: isChatsActive,
    },
    {
      path: '/projects',
      label: 'Projects',
      icon: <FolderKanban size={20} />,
      active: pathname.startsWith('/projects'),
    },
    {
      path: '/applications',
      label: 'Applications',
      icon: <Microchip size={20} />,
      hasChildren: true,
      active: applicationsChildren.some(c => c.active),
      children: applicationsChildren,
    },
    {
      path: '/data',
      label: 'Data',
      icon: <Library size={20} />,
      hasChildren: true,
      active: dataChildren.some(c => c.active),
      children: dataChildren,
    },
    {
      path: '/site',
      label: 'Site',
      icon: <Settings size={20} />,
      hasChildren: true,
      active: siteChildren.some(c => c.active),
      children: siteChildren,
    },
    {
      path: '/research',
      label: 'Research',
      icon: <Microscope size={20} />,
      active: pathname.startsWith('/research'),
    },
    {
      path: '/partnership',
      label: 'Partnership',
      icon: <Users size={20} />,
      active: pathname.startsWith('/partnership'),
    },
    {
      path: '/maplept',
      label: 'Our Models',
      icon: <Waypoints size={20} />,
      active: pathname.startsWith('/maplept'),
    },
    {
      path: '#',
      label: 'Report a Bug',
      icon: <Bug size={20} />,
      onClick: () => {
        // Try opening inside MapleOS as a window
        window.dispatchEvent(new CustomEvent('mapleos-open-app', {
          detail: { appId: 'bug-report', title: 'Report a Bug' }
        }));
        // Fallback: if MapleOS isn't active, show the regular modal
        const isMapleOSActive = !!document.querySelector('.mapleos-root');
        if (!isMapleOSActive) {
          setShowBugReport(true);
        }
      },
      active: false,
    },
    {
      path: '/desktop',
      label: 'Desktop',
      icon: <Monitor size={20} />,
      active: pathname.startsWith('/desktop'),
    },
  ];

  return baseItems;
};